import { Button, FormControl, FormLabel, Input, Text } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkLicenseIdDuplicate, uploadFile } from "../../../api";
import { colors } from "../../../styles";
import { ISignUpLicense } from "../../../types";
import { getApiErrorCode } from "../../../utils";
import { getSignUpErrorMessage } from "./errors";
import FileAttach from "./FileAttach";
import StepLayout, { FieldMessage, underlineInputProps } from "./StepLayout";

interface ILicenseInfoProps {
    progress: number;
    setLicenseData: React.Dispatch<React.SetStateAction<Partial<ISignUpLicense>>>;
    onNext: () => void;
}

interface ILicenseForm {
    number: string;
}

const licenseIdRegex = /^\d{1,6}$/;

/* 개국 약사 / 약사 공통. License 객체(번호, 면허증)를 만든다. */

export default function LicenseInfo({ progress, setLicenseData, onNext }: ILicenseInfoProps) {

    /* 로딩 상태, 에러 메시지, 디바운스된 면허 번호 */

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [licenseImage, setLicenseImage] = useState<File | null>(null);
    const [debouncedLicenseId, setDebouncedLicenseId] = useState("");

    /* useForm */

    const { register, handleSubmit, watch } = useForm<ILicenseForm>();

    const licenseId = watch("number") ?? "";
    const isLicenseIdFormatValid = licenseIdRegex.test(licenseId);

    /* 디바운스된 면허 번호 */

    useEffect(() => {
        const id = setTimeout(() => setDebouncedLicenseId(licenseId), 400);
        return () => clearTimeout(id);
    }, [licenseId]);

    /* 면허 번호 중복 확인 쿼리 */

    const { isLoading: isCheckingLicenseId, isError: isLicenseIdCheckError, error: licenseIdCheckError } = useQuery({
        queryKey: ["checkLicenseIdDuplicate", debouncedLicenseId],
        queryFn: () => checkLicenseIdDuplicate({ licenseId: debouncedLicenseId }),
        enabled: !!debouncedLicenseId && licenseIdRegex.test(debouncedLicenseId),
        retry: false,
    });

    /* 면허 번호 중복 확인 결과 */

    const isLicenseIdDuplicate = isLicenseIdCheckError && getApiErrorCode(licenseIdCheckError) === "LICENSE_ID_ALREADY_USED";
    const isLicenseIdCheckUnavailable = isLicenseIdCheckError && !isLicenseIdDuplicate;

    /* 면허 번호 중복 확인 뮤테이션 */

    const checkLicenseIdDuplicateMutation = useMutation({
        mutationFn: checkLicenseIdDuplicate,
        retry: false,
    });

    /* 면허증 업로드 뮤테이션 */

    const uploadFileMutation = useMutation({
        mutationFn: uploadFile,
    });

    /* 폼 제출 핸들러 */

    const onSubmit = async ({ number }: ILicenseForm) => {

        /* 면허증 사진이 없으면 에러 메시지 설정 */

        if (!licenseImage) {
            setErrorMessage("약사 면허증 사진을 첨부해주세요.");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {

            /* 면허 번호 중복 확인 */

            await checkLicenseIdDuplicateMutation.mutateAsync({ licenseId: number });

            /* 면허증 업로드 */

            const image = await uploadFileMutation.mutateAsync({ file: licenseImage });

            /* 면허 번호 + 면허증 URL을 License 객체로 저장 */

            setLicenseData((prev) => ({
                ...prev,
                number,
                image,
            }));

            /* 다음 화면으로 이동 */

            onNext();
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약사 정보 확인 중 문제가 발생했습니다."));
        } finally {
            setIsLoading(false);
        }
    };

    /* 면허 번호 힌트 */

    const licenseHint = (() => {
        if (!licenseId) return null;
        if (!isLicenseIdFormatValid) {
            return { message: "약사 면허 번호 형식이 올바르지 않습니다.", tone: "error" as const };
        }
        if (isCheckingLicenseId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isLicenseIdDuplicate) {
            return { message: "이미 사용 중인 약사 면허 번호입니다.", tone: "error" as const };
        }
        if (isLicenseIdCheckUnavailable) {
            return { message: "약사 면허 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 약사 면허 번호입니다.", tone: "success" as const };
    })();

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={["약사 정보를 입력해주세요."]}
            asForm
            onSubmit={handleSubmit(onSubmit)}
        >

            {/* 면허 번호 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>약사 면허 번호</FormLabel>
                <Input
                    {...register("number", { required: true })}
                    type="text"
                    placeholder="약사 면허 번호"
                    {...underlineInputProps}
                />
                <FieldMessage message={licenseHint?.message} tone={licenseHint?.tone} />
            </FormControl>

            {/* 면허증 업로드 폼 */}

            <FormControl marginTop={8}>
                <FormLabel fontSize="sm" color={colors.blue}>약사 면허증</FormLabel>
                <FileAttach
                    label="01 | 약사 면허증"
                    inputId="license-upload"
                    file={licenseImage}
                    onChange={(file) => {
                        setLicenseImage(file);
                        setErrorMessage("");
                    }}
                />
            </FormControl>

            {/* 에러 메시지 */}

            <FieldMessage message={errorMessage} />

            {/* 다음 버튼 */}

            <Button
                type="submit"
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={10}
                isLoading={isLoading}
                disabled={
                    !isLicenseIdFormatValid ||
                    isCheckingLicenseId ||
                    isLicenseIdCheckError ||
                    !licenseImage
                }
            >
                <Text fontSize={16} fontWeight={700}>다음</Text>
            </Button>
        </StepLayout>
    );
}
